import { Tag, TagAssignResponse, TagCreateInput, TagUpdateInput } from '@vivport/backend/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  assignTagToContent,
  createTag,
  deleteTag,
  getTag,
  getTagContents,
  listTags,
  reorderTags,
  removeTagFromContent,
  updateTag,
} from '../api/tag-api';
import { contentKeys } from './useContentQueries';

/**
 * Tag Query Keys Factory
 * Centralized query key factory for tag-related React Query hooks
 */
export const tagKeys = {
  all: ['tag'] as const,
  lists: () => [...tagKeys.all, 'list'] as const,
  list: (filters?: 'custom' | 'all') => [...tagKeys.lists(), filters || 'all'] as const,
  details: () => [...tagKeys.all, 'detail'] as const,
  detail: (id: number) => [...tagKeys.details(), id] as const,
  contents: (tagId: number) => [...tagKeys.all, 'contents', tagId] as const,
  contentsCount: (tagId: number) => [...tagKeys.all, 'contentsCount', tagId] as const,
  contentsSelectedCount: (tagId: number, contentIds: number[]) => [...tagKeys.all, 'contentsSelectedCount', tagId, contentIds.sort((a, b) => a - b)] as const,
};

/**
 * Custom Tags List Query Hook
 * Returns IDs only while caching full items in detail queries
 * Note: listTags() returns all non-system tags (equivalent to custom tags)
 */
export const useTagsList = () => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: tagKeys.list('custom'),
    queryFn: async (): Promise<{ ids: number[] }> => {
      const tags: Tag[] = await listTags();
      
      // Cache each tag in detail query
      tags.forEach((tag) => {
        queryClient.setQueryData(tagKeys.detail(tag.id), tag);
      });

      // Return only IDs
      return {
        ids: tags.map(tag => tag.id),
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useTags = () => {
  return useQuery({
    queryKey: tagKeys.list('all'),
    queryFn: listTags,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Tag Detail Query Hook
 * Pre-populated by list queries, fetches individual tags by ID
 */
export const useTagDetail = (id: number) => {
  return useQuery({
    queryKey: tagKeys.detail(id),
    queryFn: () => getTag(id),
    enabled: id > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Tag Contents Query Hook
 * Get content IDs associated with a specific tag
 * Returns IDs only
 */
export const useTagContents = (tagId: number) => {
  return useQuery({
    queryKey: tagKeys.contents(tagId),
    queryFn: () => getTagContents(tagId),
    enabled: tagId > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Tag Content Count Query Hook
 * Get the count of content items tagged with a specific tag
 */
export const useTagContentsCount = (tagId: number) => {
  return useQuery({
    queryKey: tagKeys.contentsCount(tagId),
    queryFn: async (): Promise<{ count: number }> => {
      const result = await getTagContents(tagId);
      return { count: result.ids.length };
    },
    enabled: tagId > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Tag Selected Contents Count Query Hook
 * Get the count of how many selected content items have a specific tag
 */
export const useTagContentsSelectedCount = (tagId: number, contentIds: number[]) => {
  return useQuery({
    queryKey: tagKeys.contentsSelectedCount(tagId, contentIds),
    queryFn: async (): Promise<{ count: number }> => {
      const result = await getTagContents(tagId);
      const filteredIds = result.ids.filter(id => contentIds.includes(id));
      return { count: filteredIds.length };
    },
    enabled: tagId > 0 && contentIds.length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Helper function to invalidate queries after tag assignment/unassignment
 */
const invalidateTagContentQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  tagId: number,
  contentIds: number[]
) => {
  // Invalidate content details for all affected items
  contentIds.forEach((contentId) => {
    queryClient.invalidateQueries({ 
      queryKey: contentKeys.detail(contentId)
    });
  });
  
      // Invalidate tag contents queries for the affected tag
      queryClient.invalidateQueries({ queryKey: tagKeys.contents(tagId) });
      // Invalidate tag content count for the affected tag
      queryClient.invalidateQueries({ queryKey: tagKeys.contentsCount(tagId) });
      // Invalidate selected content count queries for the affected tag (matches all contentIds variations)
      queryClient.invalidateQueries({ queryKey: [...tagKeys.all, 'contentsSelectedCount', tagId], exact: false });
};

/**
 * Assign Tag to Contents Mutation Hook
 * Updates content details for affected items
 */
function invalidateProcessQueries(queryClient: ReturnType<typeof useQueryClient>): void {
  queryClient.invalidateQueries({ queryKey: ['processes'] });
  queryClient.invalidateQueries({ queryKey: ['process-runs'], exact: false });
  queryClient.invalidateQueries({ queryKey: ['process-run-entries'], exact: false });
}

export const useAssignTagToContents = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tagId, contentIds }: { tagId: number; contentIds: number[] }): Promise<TagAssignResponse> =>
      assignTagToContent(tagId, contentIds),
    onSuccess: (result, { tagId, contentIds }) => {
      invalidateTagContentQueries(queryClient, tagId, contentIds);
      if (result.automation.createdProcessRunCount > 0) {
        invalidateProcessQueries(queryClient);
      }
    },
  });
};

/**
 * Unassign Tag from Contents Mutation Hook
 * Updates content details for affected items
 */
export const useUnassignTagFromContents = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tagId, contentIds }: { tagId: number; contentIds: number[] }) =>
      removeTagFromContent(tagId, contentIds),
    onSuccess: (_, { tagId, contentIds }) => {
      invalidateTagContentQueries(queryClient, tagId, contentIds);
    },
  });
};

export const useTagCreate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TagCreateInput) => createTag(data),
    onSuccess: (created) => {
      queryClient.setQueryData(tagKeys.detail(created.id), created);
      queryClient.invalidateQueries({ queryKey: tagKeys.lists() });
      queryClient.invalidateQueries({ queryKey: [...tagKeys.all, 'contents'] });
    },
  });
};

export const useTagUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: TagUpdateInput }) => updateTag(id, data),
    onSuccess: (updated) => {
      queryClient.setQueryData(tagKeys.detail(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: tagKeys.lists() });
      queryClient.invalidateQueries({ queryKey: [...tagKeys.all, 'contents'] });
    },
  });
};

export const useTagDelete = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteTag(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: tagKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: tagKeys.lists() });
      queryClient.invalidateQueries({ queryKey: [...tagKeys.all, 'contents'] });
    },
  });
};

export const useTagReorder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tagIds, tagGroupId }: { tagIds: number[]; tagGroupId?: number | null }) =>
      reorderTags({ tagIds, tagGroupId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.lists() });
    },
  });
};
